import React from "react"

import { Tooltip } from "@coval/ui/tooltip"
import {
  Ban,
  Check,
  Clock,
  RefreshCw,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react"

import { cn } from "@/utils/cn"

export const RUN_STATUS = {
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  IN_PROGRESS: "IN PROGRESS",
  IN_QUEUE: "IN QUEUE",
  CANCELLED: "CANCELLED",
} as const

const EXECUTION_FAILURE_STATUS = "EXECUTION FAILURE" as const
const PENDING_STATUS = "PENDING" as const
export const RUNNING_STATUS = "RUNNING" as const
const QUEUED_STATUS = "QUEUED" as const

type RunLifecycleStatus =
  | (typeof RUN_STATUS)[keyof typeof RUN_STATUS]
  | typeof EXECUTION_FAILURE_STATUS

interface RunStatusMeta {
  label: string
  badgeClasses: string
  isInProgress?: boolean
}

const DEFAULT_RUN_STATUS_META: RunStatusMeta = {
  label: "",
  badgeClasses: "bg-surface-1 text-coval-text",
}

const RUN_STATUS_META: Record<RunLifecycleStatus, RunStatusMeta> = {
  [EXECUTION_FAILURE_STATUS]: {
    label: "Some components failed to execute",
    badgeClasses: "bg-surface-1 text-coval-text",
  },
  [RUN_STATUS.COMPLETED]: {
    label: "Completed",
    badgeClasses: "bg-coval-success-surface text-coval-success-on-surface",
  },
  [RUN_STATUS.FAILED]: {
    label: "Failed",
    badgeClasses: "bg-coval-danger-surface text-coval-danger-on-surface",
  },
  [RUN_STATUS.CANCELLED]: {
    label: "Cancelled",
    // Cancelled is a neutral outcome rather than an error or warning, so it
    // reads as a muted gray chip instead of a red or amber one.
    badgeClasses: "bg-surface-1 text-coval-text",
  },
  [RUN_STATUS.IN_QUEUE]: {
    label: "In queue",
    badgeClasses: "bg-coval-warning-surface text-coval-warning-on-surface",
    isInProgress: true,
  },
  [RUN_STATUS.IN_PROGRESS]: {
    label: "In progress",
    badgeClasses: "bg-coval-info-surface text-coval-info-on-surface",
    isInProgress: true,
  },
}

export const normalizeRunStatus = (status: string) => {
  const normalizedStatus = (status || "")
    .replace(/_/g, " ")
    .trim()
    .toUpperCase()

  switch (normalizedStatus) {
    case RUNNING_STATUS:
      return RUN_STATUS.IN_PROGRESS
    case QUEUED_STATUS:
    case PENDING_STATUS:
      return RUN_STATUS.IN_QUEUE
    default:
      return normalizedStatus
  }
}

const getRunStatusMeta = (status: string): RunStatusMeta | null => {
  const normalizedStatus = normalizeRunStatus(status)
  return (
    RUN_STATUS_META[normalizedStatus as RunLifecycleStatus] ??
    (normalizedStatus ? DEFAULT_RUN_STATUS_META : null)
  )
}

export const getStatusBadgeClasses = (status: string): string => {
  return (
    getRunStatusMeta(status)?.badgeClasses ??
    DEFAULT_RUN_STATUS_META.badgeClasses
  )
}

export const getStatusLabel = (status: string): string => {
  const normalizedStatus = normalizeRunStatus(status)
  const label = getRunStatusMeta(status)?.label
  if (label) {
    return label
  }
  return (
    normalizedStatus.charAt(0).toUpperCase() +
    normalizedStatus.slice(1).toLowerCase()
  )
}

export const isRunInProgress = (status: string) => {
  return getRunStatusMeta(status)?.isInProgress === true
}

export const isRunTerminal = (status: string): boolean => {
  const meta = RUN_STATUS_META[normalizeRunStatus(status) as RunLifecycleStatus]
  return Boolean(meta && !meta.isInProgress)
}

export const DEFAULT_STATUS = PENDING_STATUS

export const RUN_RESULT_STATUSES = [
  ...Object.values(RUN_STATUS),
  DEFAULT_STATUS,
] as const

// Filter values for evals_run.status in the runs table.
export const RUN_STATUSES = [
  RUN_STATUS.COMPLETED,
  RUN_STATUS.FAILED,
  RUNNING_STATUS,
  DEFAULT_STATUS,
  RUN_STATUS.CANCELLED,
  QUEUED_STATUS,
] as const

interface StatusIconProps {
  status: string
  className?: string
  size?: number
  isErrorStatus?: boolean
}

// Most statuses render as a filled circle: an accent background with the glyph
// in a tone that matches the theme so it reads clearly against the fill.
// `fillClass` is the accent border token — the strong shade in light mode, the
// softer mid tone in dark; `glyphClass` is the mode-matched foreground. A visual
// may instead supply a `Custom` renderer that draws its own self-contained mark
// (e.g. the caution triangle), in which case the circle wrapper is skipped.
interface StatusIconVisual {
  Glyph?: LucideIcon
  // When set, render the solid caution triangle in this text-color token
  // (its accent border shade) instead of the circular badge.
  triangleClass?: string
  fillClass?: string
  glyphClass?: string
  // Shrink the filled circle to this fraction of `size` (default 1) so it can
  // match the visible diameter of a bare glyph such as the queue clock.
  discScale?: number
}

// The queue clock's outer ring is ~0.92 of its box (r=10 plus stroke in a 24
// viewBox); matching the filled circles to it keeps the circular states the
// same visual size.
const QUEUE_CIRCLE_SCALE = 0.92

// Solid caution triangle filled with `currentColor` (set by the caller via a
// text-color class — the accent border token, strong in light mode and mid in
// dark), with the exclamation knocked out to the surface color so it reads as
// cut out of the fill.
const SolidTriangleIcon: React.FC<{ size: number; className?: string }> = ({
  size,
  className,
}) => (
  <svg
    width={size}
    height={size}
    // The triangle glyph spans only ~81% of the stock 0 0 24 24 box, leaving it
    // narrower than the circle badges. Frame it with a tight viewBox so it fills
    // nearly the full width, keeping a small margin so the rounded corners
    // aren't shaved by the SVG's overflow clip.
    viewBox="1.8 1.8 20.4 20.4"
    fill="none"
    aria-hidden="true"
    className={cn("shrink-0", className)}
  >
    <path
      d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"
      fill="currentColor"
    />
    <path
      d="M12 10v3.5"
      stroke="var(--surface-default)"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16.9" r="1.3" fill="var(--surface-default)" />
  </svg>
)

// White in light mode, near-black in dark mode — reads clearly on the light-mode
// strong fill and the dark-mode mid fill.
const MODE_GLYPH = "text-coval-text-on-contrast"

const SUCCESS_VISUAL = {
  fillClass: "bg-coval-success-border",
  // Lucide's check path sits optically high inside its square viewBox.
  glyphClass: cn(MODE_GLYPH, "translate-y-px"),
}
const DANGER_VISUAL = {
  fillClass: "bg-coval-danger-border",
  glyphClass: MODE_GLYPH,
}
// Hard failures use the caution triangle in red (strong in light, mid in dark).
const DANGER_TRIANGLE_VISUAL = {
  triangleClass: "text-coval-danger-border",
}
// Partial failures read as a caution, not an error — the same triangle in amber.
const WARNING_VISUAL = {
  triangleClass: "text-coval-warning-border",
}
const MUTED_VISUAL = {
  fillClass: "bg-surface-1",
  glyphClass: "text-coval-text-muted",
}
// Cancelled reads as a neutral outcome, not an error — a bare "no entry" ring
// in the muted gray tone, sized to fill its box so the ring is the outer edge
// (like the queue clock) rather than sitting inside a filled disc.
const CANCELLED_VISUAL = {
  Glyph: Ban,
  glyphClass: "text-coval-text-muted",
}
// Queued / pending states are passive — a bare muted clock with no badge
// behind it (no `fillClass`), sized up to match the filled peers.
const QUEUE_VISUAL = {
  Glyph: Clock,
  glyphClass: "text-coval-text-muted",
}
// In-progress states — a bare refresh glyph in the info blue (no badge).
const PROGRESS_VISUAL = {
  Glyph: RefreshCw,
  glyphClass: "text-coval-info-border",
}

const getStatusIconVisual = (
  status: string,
  isErrorStatus: boolean,
): StatusIconVisual => {
  const normalizedStatus = (status || "")
    .replace(/_/g, " ")
    .trim()
    .toUpperCase()

  switch (normalizedStatus) {
    case RUN_STATUS.COMPLETED:
      return { Glyph: Check, ...SUCCESS_VISUAL, discScale: QUEUE_CIRCLE_SCALE }
    case RUN_STATUS.FAILED:
    case "ERROR":
    case "UNEXPECTED ERROR":
      return { ...DANGER_TRIANGLE_VISUAL }
    // Partial failures — some metrics missing or some components failed to
    // execute — are a caution, not a run-level error, so they render amber.
    case "MISSING RESULTS":
    case "EXECUTION FAILURE":
      return { ...WARNING_VISUAL }
    case RUN_STATUS.IN_QUEUE:
    case PENDING_STATUS:
      return { ...QUEUE_VISUAL }
    case RUN_STATUS.IN_PROGRESS:
    case RUNNING_STATUS:
      return { ...PROGRESS_VISUAL }
    case RUN_STATUS.CANCELLED:
      return { ...CANCELLED_VISUAL }
    case "DELETED":
      return { Glyph: X, ...MUTED_VISUAL }
    default:
      return isErrorStatus
        ? { Glyph: TriangleAlert, ...DANGER_VISUAL }
        : { Glyph: RefreshCw, ...MUTED_VISUAL }
  }
}

const getStatusIcon = (
  status: string,
  className?: string,
  size = 16,
  isErrorStatus = false,
): React.ReactNode => {
  const { Glyph, triangleClass, fillClass, glyphClass, discScale } =
    getStatusIconVisual(status, isErrorStatus)
  if (triangleClass) {
    return (
      <SolidTriangleIcon size={size} className={cn(triangleClass, className)} />
    )
  }
  // No badge fill — render the glyph on its own at full size so it matches the
  // footprint of the filled peers.
  if (!fillClass && Glyph) {
    return (
      <Glyph
        className={cn("shrink-0", glyphClass, className)}
        size={size}
        strokeWidth={2.25}
        aria-hidden="true"
      />
    )
  }
  const discSize = Math.round(size * (discScale ?? 1))
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        fillClass,
        className,
      )}
      style={{ width: discSize, height: discSize }}
    >
      {Glyph ? (
        <Glyph
          className={glyphClass}
          size={Math.round(discSize * 0.6)}
          strokeWidth={4}
        />
      ) : null}
    </div>
  )
}

// Success-equivalent statuses that should NOT override the lifecycle status.
const SUCCESS_STATUSES = new Set([
  "COMPLETED",
  "SUCCESS",
  "SUCCESSFUL",
  "PASSED",
])

/**
 * Determine the displayed status for a run. If `errorStatus` is present and
 * not a success-equivalent value, return the normalized `errorStatus` so the
 * icon and label helpers can render the correct error/warning state.
 * Otherwise fall back to the lifecycle `status` which has already been
 * normalized.
 */
export const getDisplayedRunStatus = (
  status: string,
  errorStatus?: string | null,
): string => {
  const normalizedError = (errorStatus ?? "")
    .replace(/_/g, " ")
    .trim()
    .toUpperCase()
  // RUN_CANCELLED is an outcome value (see RunOutcomeStatus on the backend),
  // not a distinct display state - it should render identically to the
  // CANCELLED lifecycle status rather than falling through to an unmapped,
  // blank-label default.
  if (normalizedError === "RUN CANCELLED") {
    return RUN_STATUS.CANCELLED
  }
  if (normalizedError && !SUCCESS_STATUSES.has(normalizedError)) {
    return normalizedError
  }
  return status
}

/**
 * Return true when `errorStatus` is present and not a success-equivalent
 * value. This is used to signal to `StatusIcon` that the displayed status
 * came from `error_status` so the `CircleAlert` icon is used for unknown
 * values.
 */
export const hasNonSuccessRunErrorStatus = (
  errorStatus?: string | null,
): boolean => {
  const normalizedError = (errorStatus ?? "")
    .replace(/_/g, " ")
    .trim()
    .toUpperCase()
  return !!normalizedError && !SUCCESS_STATUSES.has(normalizedError)
}

export const StatusIcon: React.FC<StatusIconProps> = ({
  status,
  className,
  size = 16,
  isErrorStatus = false,
}) => {
  return (
    <Tooltip content={getStatusLabel(status)}>
      <div
        className="flex items-center justify-center"
        data-testid={`status-icon-${(status ?? "unknown")
          .trim()
          .replace(/[^a-z0-9]/gi, "-")
          .toLowerCase()}`}
      >
        {getStatusIcon(status, className, size, isErrorStatus)}
      </div>
    </Tooltip>
  )
}
